import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { BtwDialogModule, BtwDialogService } from '@between/ng-cdk/dialog';
import { BtwButtonModule } from '@between/ng-cdk/button';
import { BtwCardModule } from '@between/ng-cdk/card';
import { BtwTextBoxModule } from '@between/ng-cdk/text-box';
import { BankStatementsUploaderComponent } from '../bank-statements-uploader/bank-statements-uploader.component';
import { BtwValidatorModule } from '@between/ng-cdk/validator';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BtwIconModule } from '@between/ng-cdk/icon';
import { isGuidValidator } from '../helpers/apiKeyHelper';
import { GenericIndicatorAnalysisComponent } from '../analyses/generic-indicator-analysis/generic-indicator-analysis.component';
import { Guid } from 'guid-typescript';
import { AuthService } from '../core/services/auth.service';
import { EMPTY, debounceTime, switchMap } from 'rxjs';
import { AnalysisApiService } from '../api-services/analysis-api/analysis-api.service';
import { BtwLoadPanelModule } from '@between/ng-cdk/load-panel';
import { HttpErrorResponse } from '@angular/common/http';
import { FinancialStatementAnalysisComponent } from '../analyses/financial-statement-analysis/financial-statement-analysis.component';
import { BtwToastService } from '@between/ng-cdk/toast';
import { AuthApiService } from '../api-services/auth-api/auth-api.service';
import { BtwTooltipDirective } from '@between/ng-cdk/tooltip';
import { StackedIndicatorAnalysisComponent } from '../analyses/stacked-indicator-analysis/stacked-indicator-analysis.component';
import { BtwSelectBoxModule } from '@between/ng-cdk/select-box';
import { IIndicatorAnalysisListItemDto } from '../api-services/indicator-api/indicator-api.interfaces';
import { DailyDrillDownComponent } from '../daily-drill-down/daily-drill-down.component';
import { ModelApiService } from '../api-services/model-api/model-api.service';
import { ModelDto } from '../api-services/model-api/model-api.interfaces';
import { ModelDetailsComponent } from '../models/model-details/model-details.component';


@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    BtwButtonModule,
    BtwDialogModule,
    BankStatementsUploaderComponent,
    BtwCardModule,
    BtwTextBoxModule,
    ReactiveFormsModule,
    BtwValidatorModule,
    FormsModule,
    BtwIconModule,
    GenericIndicatorAnalysisComponent,
    StackedIndicatorAnalysisComponent,
    FinancialStatementAnalysisComponent,
    BtwLoadPanelModule,
    BtwTooltipDirective,
    BtwSelectBoxModule,
    DailyDrillDownComponent,
    ModelDetailsComponent
  ],
  providers: [DatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  private readonly authService: AuthService = inject(AuthService);
  private readonly authApiService: AuthApiService = inject(AuthApiService);
  private readonly modelApiService: ModelApiService = inject(ModelApiService);

  private readonly analysisApiService: AnalysisApiService = inject(AnalysisApiService);
  private readonly dialogService: BtwDialogService = inject(BtwDialogService);
  private readonly toastService: BtwToastService = inject(BtwToastService);
  private readonly datePipe: DatePipe = inject(DatePipe);

  @ViewChild('defaultSideBarItem') defaultSideBarItem: ElementRef | undefined;

  form: FormGroup;
  apiKeyFormControl: FormControl = new FormControl('', [Validators.required, Validators.pattern(isGuidValidator())]);
  analysisIdFormControl: FormControl = new FormControl('', [Validators.required, Validators.pattern(isGuidValidator())]);

  selectedIndicator: string | undefined;
  isProcessingFilesInProgress: boolean = false;
  selectedModelId: number;

  analysesDataSource: any[];

  modelsDataSource: ModelDto[];

  isApiKeyValid: boolean = false;

  ngOnInit(): void {
    this.initialize();
  }

  handleSideBarItemChange(indicatorname: string) {
    this.selectedIndicator = indicatorname;
    this.selectedModelId = 0;
  }

  get getModelId() {
    return this.selectedModelId;
  }

  handleOpenBankStatementsUploader(): void {
    this.dialogService.open(BankStatementsUploaderComponent, {
      title: 'Bank statements uploader',
      data: this.authService.getApiKey()
    }).afterClosed
      .pipe(
        switchMap((data) => {
          if (data) {
            this.isProcessingFilesInProgress = true;

            const { uploadReferenceId } = data;
            return this.analysisApiService.triggerAnalysisFromUploadReferenceId(uploadReferenceId)
          }

          return EMPTY;
        })
      ).subscribe({
        next: (analysisId?: Guid) => {
          if (analysisId) {
            this.analysisIdFormControl.setValue(analysisId);

            this.setIndicatorAnalysesDataSource();
            this.resetSideBarSelection();
          }

          this.isProcessingFilesInProgress = false;
        },
        error: (err: HttpErrorResponse) => {
          this.isProcessingFilesInProgress = false;
        }
      });
  }

  private initialize(): void {
    this.form = new FormGroup({
      apiKey: this.apiKeyFormControl,
      analysisId: this.analysisIdFormControl
    });

    const apiKeyStr = localStorage.getItem('api-key') || null;
    const apiKey = apiKeyStr ? Guid.parse(apiKeyStr) : null;
    this.isApiKeyValid = apiKey ? true : false;

    this.apiKeyFormControl.setValue(apiKey);
    this.authService.setApiKey(apiKey);

    this.setIndicatorAnalysesDataSource();
    
    this.setModelsDataSource();

    this.apiKeyFormControl.valueChanges.pipe(debounceTime(500)).subscribe(value => {
      if (!this.apiKeyFormControl.valid) {
        return;
      }

      this.authApiService.checkApiKey(value).subscribe({
        next: result => {
          this.isApiKeyValid = result;
          this.authService.setApiKey(result ? value : null);

          if (result) {
            localStorage.setItem('api-key', value);
            this.setIndicatorAnalysesDataSource();
          } else {
            this.toastService.error('Invalid API Key. Please verify your API key is valid.');
          }
        }
      });
    });

    this.analysisIdFormControl.valueChanges.subscribe(value => this.resetSideBarSelection());
  }

  private resetSideBarSelection(): void {
    this.selectedIndicator = '';

    setTimeout(() => {
      this.defaultSideBarItem?.nativeElement.click();
    }, 500);
  }

  private setModelsDataSource(): void {
    if (!this.authService.getApiKey()) {
      return;
    }

    this.modelApiService.getAll().subscribe(res => {
      this.modelsDataSource = res;
    });
  }

  private setIndicatorAnalysesDataSource(): void {
    if (!this.authService.getApiKey()) {
      return;
    }

    this.analysisApiService.getAll().subscribe({
      next: (response: IIndicatorAnalysisListItemDto[]) => {
        const { value } = this.analysisIdFormControl;
        if (!value && response.length > 0) {
          this.analysisIdFormControl.setValue(response[0].id);
        }

        // TODO: leave only id here, not company name
        this.analysesDataSource = response.map(item => ({ value: item.id, label: `${this.datePipe.transform(item.createdAt, 'yyyy/MM/dd hh:mm:ss')} - ${item.companyName}` }));
      }
    });
  }

  get selectedAnalysisId(): Guid {
    return this.analysisIdFormControl.value;
  }

  get isIndicatorDisabled(): boolean {
    return !this.apiKeyFormControl.valid || !this.analysisIdFormControl.valid;
  }

  handleModelChange(model: ModelDto) {
    this.selectedIndicator = "Model";
    this.selectedModelId = model.id;
  }
}
